import { Component, CSSProperties, ErrorInfo, ReactNode } from 'react';
import { reportError } from './reportError';

/* Last-resort fallback styling is deliberately inline (not design tokens):
   the fallback must render with zero dependencies on the Stencil component
   library or theme context, which may themselves be the source of the crash.
   standards-ignore: dependency-free */
const FALLBACK_STYLE: CSSProperties = { padding: '1.5rem', textAlign: 'center' };

interface Props {
  children: ReactNode;
  /** Optional fallback UI. Defaults to a generic error message. */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Generic error boundary for v2 pages.
 * Catches render errors, reports them via reportError(), and shows a fallback UI.
 * Place one at the app root and optionally one per page.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    reportError(error, { componentStack: errorInfo.componentStack ?? undefined });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          /* Last-resort fallback: deliberately plain HTML and untranslated
             copy (standards-ignore: i18n-exempt). The crash may originate in
             the i18n layer (so no useMessages) or in the Stencil component
             library / theme context itself (so no Col/H2/Text) — a throw
             during this render would unmount the entire tree. The style
             prop with a hoisted const (not an inline object literal) is
             legal under the v2 inline-style lint rule. */
          <div role="alert" style={FALLBACK_STYLE}>
            <h2>{'Something went wrong'}</h2>
            <p>{'Please refresh the page or contact support if the problem persists.'}</p>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
